import {
  Modal,
  Skeleton,
  Group,
  Stack,
  Text,
  TextInput,
  ScrollArea,
} from '@mantine/core';
import { useInView } from 'react-intersection-observer';
import BigNumber from 'bignumber.js';
import cn from 'clsx';
import { LiaSearchSolid } from 'react-icons/lia';
import { BiLinkExternal } from 'react-icons/bi';
import { useState } from 'react';
import { useTokensInfo, useUserBalances, useTokensPrices } from 'src/api/dex';
import { rangeFromZero } from 'src/utils';
import { NETWORK_VARS } from 'src/constants';
import { Token } from 'src/components/Token/Token';
import { VerifiedIcon } from 'src/assets/VerifiedIcon';
import { TokenInfo } from 'src/types';

const TOKEN_HEIGHT = 52;
const MAX_TOKENS_VISIBLE = 5;
const TOKEN_GAP = 8;

export function TokensModal({
  opened,
  selectedToken,
  onClose,
  onSelectToken,
}: {
  opened: boolean;
  selectedToken?: string;
  onClose: () => void;
  onSelectToken: (token: string) => void;
}) {
  const [search, setSearch] = useState('');

  const { tokensInfo, isLoadingTokensInfo } = useTokensInfo();
  const { userBalancesMapping, isLoadingUserBalances } = useUserBalances();
  const { tokensPrices, isLoadingTokensPrices } = useTokensPrices();

  const handleClose = () => {
    setSearch('');
    onClose();
  };

  let filteredTokens: TokenInfo[] = [];

  if (tokensInfo?.length) {
    let tokensWithBalance: TokenInfo[] = [];
    let tokensWithoutBalance: TokenInfo[] = [];

    for (let i = 0; i < tokensInfo.length; i++) {
      const token = tokensInfo[i];

      if (search) {
        const filteringCondition =
          token.name.toLowerCase().includes(search.toLowerCase()) ||
          token.symbol.toLowerCase().includes(search.toLowerCase()) ||
          token.contract_address.toLowerCase().includes(search.toLowerCase());

        if (!filteringCondition) {
          continue;
        }
      }

      const tokenBalance =
        userBalancesMapping?.[token.contract_address.toLowerCase()]?.balance;

      if (BigNumber(tokenBalance ?? 0).gt(0)) {
        tokensWithBalance.push(token);
      } else {
        tokensWithoutBalance.push(token);
      }
    }

    if (!isLoadingTokensPrices && tokensPrices) {
      tokensWithBalance = tokensWithBalance.sort((a, b) => {
        const tokenAWealth = BigNumber(
          tokensPrices[a.contract_address] || 0
        ).times(
          userBalancesMapping?.[a.contract_address.toLowerCase()]?.balance ?? 0
        );
        const tokenBWealth = BigNumber(
          tokensPrices[b.contract_address] || 0
        ).times(
          userBalancesMapping?.[b.contract_address.toLowerCase()]?.balance ?? 0
        );

        return tokenBWealth.lt(tokenAWealth) ? -1 : 1;
      });
    }

    filteredTokens = [...tokensWithBalance, ...tokensWithoutBalance];
  }

  const renderTokens = () => {
    if (isLoadingTokensInfo || isLoadingUserBalances || !tokensInfo?.length) {
      return rangeFromZero(5).map((i) => (
        <Group key={i} className="h-[50px]">
          <Skeleton h={42} />
        </Group>
      ));
    }

    return filteredTokens?.map((token) => {
      const tokenBalance =
        userBalancesMapping?.[token.contract_address.toLowerCase()]?.balance;

      return (
        <VirtualScrollItem key={token.contract_address}>
          <Group
            h={TOKEN_HEIGHT}
            gap={16}
            py={8}
            px={8}
            justify="space-between"
            wrap="nowrap"
            className={cn(
              'cursor-pointer hover:bg-[rgba(154,238,230,0.02)]',
              selectedToken === token.contract_address &&
                'bg-[rgba(154,238,230,0.02)]'
            )}
            onClick={() => {
              setSearch('');
              onSelectToken(token.contract_address);
            }}
          >
            <Group gap={8} wrap="nowrap" className="min-w-0">
              <Token.Icon address={token.contract_address} size={32} />

              <div className="grow min-w-0">
                <Group gap={2}>
                  <Text fz={16} fw={600} className="min-w-0" truncate>
                    {token.symbol}
                  </Text>

                  {token.verified && <VerifiedIcon className="w-4 h-4" />}
                </Group>
                <Group gap={6} mt={2}>
                  <Text fz={12} fw={500} c="#7B7B7B">
                    {token.name}
                  </Text>

                  <TokenAddress address={token.contract_address} />
                </Group>
              </div>
            </Group>

            <div className="shrink-0">
              <Token.Balance
                tokenInfo={token}
                value={tokenBalance}
                useSCBalance={false}
                underline={false}
                withSymbol={false}
                withIcon={false}
                emptyIfZero
                textProps={{ c: 'var(--mantine-color-main-1)' }}
                groupProps={{ className: 'justify-end' }}
              />
              <div className="h-[18px]">
                <Token.Price
                  tokenInfo={token}
                  amount={tokenBalance}
                  textProps={{ ta: 'right' }}
                  emptyIfZero
                />
              </div>
            </div>
          </Group>
        </VirtualScrollItem>
      );
    });
  };

  const numOfVisibleTokens = Math.min(
    MAX_TOKENS_VISIBLE,
    filteredTokens?.length ?? 0
  );
  const tokensAreaHeight =
    TOKEN_HEIGHT * numOfVisibleTokens + TOKEN_GAP * (numOfVisibleTokens - 1);

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      size={480}
      withCloseButton={false}
      classNames={{
        title: 'text-[32px] font-[600]',
        header: 'border-none !pb-0 mb-8',
        content: '!px-0 !py-0 !rounded-[24px] !bg-[#151519]',
      }}
    >
      <TextInput
        placeholder="Enter the token symbol or address"
        h={56}
        mb={16}
        classNames={{
          root: 'border-b border-solid border-transparent border-b-[#202027]',
          wrapper: 'h-full',
          input: 'h-full pl-[48px] pr-[52px] border-none bg-transparent',
        }}
        leftSection={<LiaSearchSolid size={24} className="ml-3" />}
        leftSectionWidth={44}
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
      />

      <div
        className="absolute right-5 top-4 cursor-pointer"
        onClick={handleClose}
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M12 2.25C6.615 2.25 2.25 6.615 2.25 12C2.25 17.385 6.615 21.75 12 21.75C17.385 21.75 21.75 17.385 21.75 12C21.75 6.615 17.385 2.25 12 2.25ZM10.28 9.22C10.2113 9.14631 10.1285 9.08721 10.0365 9.04622C9.94454 9.00523 9.84522 8.98318 9.74452 8.98141C9.64382 8.97963 9.54379 8.99816 9.4504 9.03588C9.35701 9.0736 9.27218 9.12974 9.20096 9.20096C9.12974 9.27218 9.0736 9.35701 9.03588 9.4504C8.99816 9.54379 8.97963 9.64382 8.98141 9.74452C8.98318 9.84522 9.00523 9.94454 9.04622 10.0365C9.08721 10.1285 9.14631 10.2113 9.22 10.28L10.94 12L9.22 13.72C9.14631 13.7887 9.08721 13.8715 9.04622 13.9635C9.00523 14.0555 8.98318 14.1548 8.98141 14.2555C8.97963 14.3562 8.99816 14.4562 9.03588 14.5496C9.0736 14.643 9.12974 14.7278 9.20096 14.799C9.27218 14.8703 9.35701 14.9264 9.4504 14.9641C9.54379 15.0018 9.64382 15.0204 9.74452 15.0186C9.84522 15.0168 9.94454 14.9948 10.0365 14.9538C10.1285 14.9128 10.2113 14.8537 10.28 14.78L12 13.06L13.72 14.78C13.7887 14.8537 13.8715 14.9128 13.9635 14.9538C14.0555 14.9948 14.1548 15.0168 14.2555 15.0186C14.3562 15.0204 14.4562 15.0018 14.5496 14.9641C14.643 14.9264 14.7278 14.8703 14.799 14.799C14.8703 14.7278 14.9264 14.643 14.9641 14.5496C15.0018 14.4562 15.0204 14.3562 15.0186 14.2555C15.0168 14.1548 14.9948 14.0555 14.9538 13.9635C14.9128 13.8715 14.8537 13.7887 14.78 13.72L13.06 12L14.78 10.28C14.8537 10.2113 14.9128 10.1285 14.9538 10.0365C14.9948 9.94454 15.0168 9.84522 15.0186 9.74452C15.0204 9.64382 15.0018 9.54379 14.9641 9.4504C14.9264 9.35701 14.8703 9.27218 14.799 9.20096C14.7278 9.12974 14.643 9.0736 14.5496 9.03588C14.4562 8.99816 14.3562 8.97963 14.2555 8.98141C14.1548 8.98318 14.0555 9.00523 13.9635 9.04622C13.8715 9.08721 13.7887 9.14631 13.72 9.22L12 10.94L10.28 9.22Z"
            fill="#3E3E4C"
          />
        </svg>
      </div>

      <ScrollArea h={tokensAreaHeight}>
        <Stack gap={TOKEN_GAP} px={12}>
          {renderTokens()}
        </Stack>
      </ScrollArea>
    </Modal>
  );
}

function VirtualScrollItem({ children }: { children: React.ReactNode }) {
  const { ref, inView } = useInView({
    triggerOnce: false,
    rootMargin: `${TOKEN_HEIGHT * 2}px 0px`,
  });

  return (
    <div ref={ref} style={{ height: TOKEN_HEIGHT }}>
      {inView ? children : null}
    </div>
  );
}

function TokenAddress({ address }: { address: string }) {
  return (
    <Group gap={2}>
      <Text fz={12} fw={500} c="#505062">
        {address.slice(0, 5)}...{address.slice(-5)}
      </Text>
      <a
        href={`${NETWORK_VARS.explorer}/address/${address}`}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <BiLinkExternal size={12} color="#505062" />
      </a>
    </Group>
  );
}
